import { useStore } from "vuex";
import cookiesService from "@/services/cookiesService";
import userService from "@/services/userService";

/**
 * Composable for managing user state updates across the application
 * Fetches complete user profile and updates both Vuex store and cookies
 */
export function useUserState() {
  const store = useStore();

  /**
   * Update global user state in Vuex store and cookies
   * Fetches complete user profile to ensure we have all user data
   * @param {Object} basicUserData - Optional fallback user data if profile fetch fails
   */
  async function updateUserState(basicUserData = null) {
    try {
      // Fetch complete user profile to ensure we have all user data
      const response = await userService.getProfile();
      const userProfile = response.data?.data;

      if (userProfile) {
        console.log("useUserState: Updating store with complete user profile");
        // Update the store with complete user data
        store.dispatch("updateUserData", userProfile);

        // Update the cookie with complete user profile
        const currentAuthToken = await cookiesService.getCookie("authToken");
        await cookiesService.setCookie("authToken", {
          ...currentAuthToken,
          ...userProfile,
        });
      } else {
        // Fallback to basic user data if profile fetch doesn't return data
        console.log("useUserState: Using basic user data for store update");
        if (basicUserData) {
          store.dispatch("updateUserData", basicUserData);
        }
      }
    } catch (error) {
      console.error(
        "useUserState: Error fetching user profile, using basic data:",
        error
      );
      // Even if profile fetch fails, we still have basic user data
      if (basicUserData) {
        store.dispatch("updateUserData", basicUserData);
      }
    }
  }

  /**
   * Check if user is currently authenticated
   * @returns {Promise<boolean>} true if authenticated, false otherwise
   */
  async function isAuthenticated() {
    try {
      const authToken = await cookiesService.getCookie("authToken");

      // Check if token exists and has a valid token property
      if (!authToken?.token) {
        return false;
      }

      // Optionally verify token by calling getProfile
      // This also ensures the token is still valid on the server
      const response = await userService.getProfile();
      return response?.data?.data ? true : false;
    } catch (error) {
      console.error("useUserState: Error checking authentication:", error);
      return false;
    }
  }

  /**
   * Simple synchronous check for auth token existence
   * Note: This only checks if token exists locally, not if it's valid
   * @returns {Promise<boolean>} true if token exists, false otherwise
   */
  async function hasAuthToken() {
    try {
      const authToken = await cookiesService.getCookie("authToken");
      return !!authToken?.token;
    } catch (error) {
      console.error("useUserState: Error checking auth token:", error);
      return false;
    }
  }

  return {
    updateUserState,
    isAuthenticated,
    hasAuthToken,
  };
}
